import React, { useContext } from 'react';
import { NegativeTriangle, PositiveTriangle } from 'assets/svg';
import Loader from 'components/molecules/Loader';
import { colorConstants } from 'config/colorConstants';
import { KpiMetricType, priceSimulatorDataContext } from 'contexts/PriceSimulatorContext';
import _ from 'lodash';
import { LoadingOutlined } from '@ant-design/icons';

import { formatNumWithKMB, formatNumWithKMBAlt } from 'utils/helperMethods';
import { Spin } from 'antd';

type props = {
    kpiMetic: KpiMetricType;
    loading: boolean;
};

const KPICardComponent = ({ kpiMetic, loading }: props) => {
    const { name, goalValue, simulatedValue, actualValue } = kpiMetic;
    const { simulationOutputLoading } = useContext(priceSimulatorDataContext);

    const renderContent = (val: string) => (val === '' || _.isNil(val) ? '-' : val);
    const tempNewGoalValue = goalValue === '' ? actualValue : goalValue;

    return (
        <div
            className="flex w-64 grow flex-col rounded bg-white"
            style={{ minWidth: '300px', maxWidth: '400px', overflowX: 'scroll' }}
        >
            {loading ? (
                <div className="flex justify-center items-center h-3/4" style={{ height: '163.4px' }}>
                    <Loader />
                </div>
            ) : (
                <>
                    <div className="basis-1/5 pt-2 text-sm font-semibold text-greyFont">{name}</div>
                    <div className="flex basis-2/5 justify-between">
                        <div className="m-2 flex grow flex-col content-center justify-center">
                            <span className="text-base text-purpleFont">
                                {renderContent(formatNumWithKMBAlt(Number(actualValue || 0), 2, 2))}
                            </span>
                            <span className="text-fontGreyAlt1"> Actuals </span>
                        </div>
                        <div className="m-auto h-3/4 grow-0 border-r-2 border-borderGrey w-1"></div>
                        <div className="m-2 flex grow flex-col content-center justify-center">
                            <span className="text-base text-purpleFont">
                                {renderContent(formatNumWithKMBAlt(Number(tempNewGoalValue || 0), 2, 2))}
                            </span>
                            <span className="text-fontGreyAlt1"> Goal </span>
                        </div>
                        <div className="m-auto h-3/4 grow-0 border-r-2 border-borderGrey w-1"></div>

                        <div className="flex grow flex-col content-center justify-center">
                            {simulationOutputLoading ? (
                                <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                            ) : (
                                <span className="text-base text-purpleFont">
                                    {simulatedValue === ''
                                        ? renderContent(simulatedValue)
                                        : renderContent(formatNumWithKMBAlt(Number(simulatedValue), 2, 2))}
                                </span>
                            )}
                            <span className="text-fontGreyAlt1"> Simulated </span>
                        </div>
                    </div>
                    <div
                        className="flex basis-2/5 justify-between border-t-2 py-2"
                        style={{ borderColor: colorConstants.greyAlt1 }}
                    >
                        <div className="flex grow flex-col content-center justify-center">
                            <div className="flex justify-center items-center">
                                <span className="mr-2">
                                    {/* render +/- sign */}
                                    {Math.abs(Number(tempNewGoalValue) - Number(simulatedValue)) > 0 &&
                                        (Number(simulatedValue) - Number(tempNewGoalValue) >= 0 ? (
                                            <PositiveTriangle />
                                        ) : (
                                            <NegativeTriangle />
                                        ))}
                                </span>
                                {renderContent(
                                    formatNumWithKMBAlt(
                                        Number(simulatedValue) - Number(tempNewGoalValue),
                                        2,
                                        2,
                                    ).toString(),
                                )}
                            </div>
                            <span className="text-fontGreyAlt1"> Simulated vs Goal </span>
                        </div>
                        <div className="flex grow flex-col content-center justify-center">
                            <div className="flex justify-center items-center">
                                {!simulationOutputLoading && (
                                    <span className="mr-2">
                                        {/* render +/- sign */}
                                        {Math.abs(Number(simulatedValue) - Number(actualValue)) > 0 &&
                                            (Number(simulatedValue) - Number(actualValue) >= 0 ? (
                                                <PositiveTriangle />
                                            ) : (
                                                <NegativeTriangle />
                                            ))}
                                    </span>
                                )}
                                {simulationOutputLoading ? (
                                    <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                                ) : (
                                    renderContent(
                                        formatNumWithKMBAlt(
                                            Number(simulatedValue) - Number(actualValue),
                                            2,
                                            2,
                                        ).toString(),
                                    )
                                )}
                            </div>
                            <span className="text-fontGreyAlt1">Simulated vs Actuals</span>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default KPICardComponent;
